import React, { useEffect, useMemo, useState } from 'react';
import { api } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { placementColumns } from '../components/PlacementsTable';
import { ORG_LABELS, TEAM_LABELS } from '../config';
import { rangeIso } from '../dates';
import { fmtDate, fmtMoney, fmtNumber, fmtPct, useDebounce, useFetch, usePaged } from '../hooks';
import { Alert, Button, Card, Skeleton, Stat, Tabs } from '../ui/basics';
import { DataTable } from '../ui/DataTable';
import { useToast } from '../ui/overlay';
import OnboardingReport from './reports/OnboardingReport';
import ReportFilters, { DEFAULTS } from './reports/ReportFilters';

function ReportBuilder({ org, switcher }) {
  const { teams, allTeams } = useAuth();
  const toast = useToast();
  const [filters, setFilters] = useState(DEFAULTS);
  const [busy, setBusy] = useState('');
  const patch = (update) => setFilters((prev) => ({ ...prev, ...update }));
  const fixedTeam = !allTeams && teams.length === 1 ? teams[0] : '';
  const teamKeys = allTeams ? Object.keys(TEAM_LABELS) : teams;

  const [from, to] = filters.period === 'Custom' ? [filters.from, filters.to] : rangeIso(filters.period === 'All Time' ? '' : filters.period);
  const invalid = filters.period === 'Custom' && from && to && from > to ? 'The start date must be on or before the end date.' : '';
  const search = useDebounce(filters.search);
  const params = useMemo(() => ({
    organization: org, basis: filters.basis, from_date: from, to_date: to, lead: fixedTeam ? [fixedTeam] : filters.leads,
    client: filters.clients, recruiter: filters.recruiters, loc: filters.locations, status: filters.status, search,
    ...(filters.early ? { ended_early: '1', early_min_days: filters.earlyMin, early_max_days: filters.earlyMax } : {}),
  }), [org, filters.basis, from, to, filters.leads, filters.clients, filters.recruiters, filters.locations, filters.status, search, fixedTeam, filters.early, filters.earlyMin, filters.earlyMax]);
  const earlyBad = filters.early && filters.earlyMin && filters.earlyMax && Number(filters.earlyMin) > Number(filters.earlyMax);
  const ready = !invalid && !earlyBad;

  const facets = useFetch('reports/builder/facets/', { organization: org, lead: params.lead });
  const summary = useFetch(ready ? 'reports/builder/summary/' : null, params);
  const paged = usePaged(ready ? 'reports/builder/' : null, { fixed: params, initialSize: 25, initialOrdering: '-date_of_joining' });

  // Drop picked clients/recruiters that no longer exist once the team selection changes.
  useEffect(() => {
    if (!facets.data) return;
    const keep = (list, valid) => list.filter((v) => valid.includes(v));
    const places = facets.data.locations || [];
    if (keep(filters.clients, facets.data.clients).length !== filters.clients.length ||
        keep(filters.recruiters, facets.data.recruiters).length !== filters.recruiters.length ||
        keep(filters.locations, places).length !== filters.locations.length) {
      patch({ clients: keep(filters.clients, facets.data.clients), recruiters: keep(filters.recruiters, facets.data.recruiters), locations: keep(filters.locations, places) });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [facets.data]);

  const s = summary.data;
  const count = s ? s.count : 0;
  const basisWord = { started: 'started', ended: 'ended', both: 'started and ended', either: 'started or ended' }[filters.basis];
  const sentence = s && (from || to
    ? `${fmtNumber(count)} placements ${basisWord} ${from && to ? `between ${fmtDate(from)} and ${fmtDate(to)}` : from ? `from ${fmtDate(from)}` : `up to ${fmtDate(to)}`}.`
    : `${fmtNumber(count)} placements across all dates.`);

  const exportFile = async (type) => {
    setBusy(type);
    try {
      await api.download('reports/builder/export/', { ...params, ordering: paged.ordering, file_type: type },
        `placements-report-${org.replace(/\s+/g, '-')}-${new Date().toISOString().slice(0, 10)}.${type}`);
      toast(`${type.toUpperCase()} downloaded`, 'success');
    } catch (e) { toast(e.message, 'error'); }
    setBusy('');
  };
  const columns = placementColumns({ combined: org === 'combined', showProjected: filters.early, showEarly: filters.early });
  const early = s ? s.early : null;
  const earlyTeams = early ? Object.entries(early.by_team).filter(([, v]) => v.starts > 0) : [];
  const teamRows = s ? Object.entries(s.by_team).filter(([, v]) => v.count > 0) : [];

  return (
    <div className="stack">
      <div className="page-head">
        <div><h1>Reports</h1><p>{ORG_LABELS[org]} · pull placement details for any period, team, client or recruiter</p></div>
        <div className="actions">
          <Button icon="file" loading={busy === 'csv'} disabled={!ready || !count || Boolean(busy)} onClick={() => exportFile('csv')}>Export CSV</Button>
          <Button icon="file" loading={busy === 'pdf'} disabled={!ready || !count || Boolean(busy)} onClick={() => exportFile('pdf')}>Export PDF</Button>
        </div>
      </div>

      {switcher}

      <ReportFilters filters={filters} onChange={patch} facets={facets.data} teamKeys={teamKeys} fixedTeam={fixedTeam} invalid={invalid} summary={sentence} />

      {summary.error && <Alert>{summary.error}</Alert>}
      <div className="grid cols-4">
        {summary.loading && !s ? [0, 1, 2, 3].map((i) => <div key={i} className="card" style={{ padding: 20 }}><Skeleton w="40%" /><div style={{ height: 10 }} /><Skeleton h={28} w="60%" /></div>) : s && <>
          <Stat label="Placements" value={fmtNumber(s.count)} />
          <Stat label="Total gross margin" value={fmtMoney(s.total_margin)} sub={s.count ? `${fmtMoney(s.total_margin / s.count)} average` : ''} />
          <Stat label="Active" value={fmtNumber(s.active)} sub="currently placed" />
          <Stat label="Inactive" value={fmtNumber(s.inactive)} sub="ended" />
        </>}
      </div>

      {early && early.starts > 0 && (
        <Card title="Early ends" subtitle={`Placements that ended before their projected end date${filters.early ? '. The list below shows only those.' : '.'}`}>
          <div className="grid cols-4">
            <Stat label="Early ends vs starts" value={fmtPct(early.pct_of_starts)} sub={`${fmtNumber(early.early)} of ${fmtNumber(early.starts)} placements in this report`} />
            <Stat label="Early ends vs ended" value={fmtPct(early.pct_of_ended)} sub={`${fmtNumber(early.early)} of ${fmtNumber(early.ended)} that have ended`} />
            {earlyTeams.slice(0, 2).map(([t, r]) => <Stat key={t} label={`${TEAM_LABELS[t]} · vs starts`} value={fmtPct(r.pct_of_starts)} sub={`${fmtNumber(r.early)} of ${fmtNumber(r.starts)}`} />)}
          </div>
          {earlyTeams.length > 2 && <p className="muted" style={{ margin: '12px 0 0' }}>{earlyTeams.slice(2).map(([t, r]) => `${TEAM_LABELS[t]}: ${fmtPct(r.pct_of_starts)} (${r.early} of ${r.starts})`).join(' · ')}</p>}
        </Card>
      )}

      {s && s.count > 0 && (teamRows.length > 1 || s.by_organization.length > 1) && (
        <div className="grid cols-2">
          {teamRows.length > 1 && (
            <Card flush title="By team"><div className="table-wrap"><table className="table"><thead><tr><th>Team</th><th className="r">Placements</th><th className="r">Margin</th></tr></thead>
              <tbody>{teamRows.map(([t, v]) => <tr key={t}><td><b>{TEAM_LABELS[t]}</b></td><td className="r num">{v.count}</td><td className="r num">{fmtMoney(v.margin)}</td></tr>)}</tbody></table></div></Card>)}
          {s.by_organization.length > 1 && (
            <Card flush title="By organization"><div className="table-wrap"><table className="table"><thead><tr><th>Organization</th><th className="r">Placements</th><th className="r">Margin</th></tr></thead>
              <tbody>{s.by_organization.map((o) => <tr key={o.organization}><td><b>{o.organization}</b></td><td className="r num">{o.count}</td><td className="r num">{fmtMoney(o.margin)}</td></tr>)}</tbody></table></div></Card>)}
        </div>
      )}

      <Card flush title="Placements" subtitle="Click a column heading to sort. Exports use the same order.">
        {ready ? <DataTable columns={columns} paged={paged} rowKey={(r) => r.s_no} noun="placements" /> : <div style={{ padding: 20 }}><Alert tone="warning">{invalid || 'The minimum days early is above the maximum.'}</Alert></div>}
      </Card>
    </div>
  );
}

export default function Reports() {
  const { org, hasPage } = useAuth();
  const [kind, setKind] = useState('placements');
  const switcher = hasPage('onboardings')
    ? <Tabs tabs={[{ key: 'placements', label: 'Placements' }, { key: 'onboardings', label: 'Onboardings' }]} value={kind} onChange={setKind} /> : null;
  return kind === 'onboardings' ? <OnboardingReport key={org} org={org} switcher={switcher} /> : <ReportBuilder key={org} org={org} switcher={switcher} />;
}
